import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DollarSign, TrendingUp } from "lucide-react";
import { formatCost } from "@/utils/costEstimation";
import { useLanguage } from "@/contexts/LanguageContext";

interface CostTrackerProps {
  totalCost: number;
  breakdown?: {
    upload?: number;
    chatQueries?: number;
    rerunCaptions?: number;
  };
}

export function CostTracker({ totalCost, breakdown }: CostTrackerProps) {
  const { t } = useLanguage();
  
  if (totalCost === 0) {
    return null;
  }

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            {t("cost.totalAiCost")}
          </h3>
          <Badge variant="secondary" className="text-lg font-bold">
            {formatCost(totalCost)}
          </Badge>
        </div>

        {breakdown && (
          <div className="space-y-1.5 text-xs border-t pt-2">
            {breakdown.upload !== undefined && breakdown.upload > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t("cost.initialProcessing")}</span>
                <span className="font-medium">{formatCost(breakdown.upload)}</span>
              </div>
            )}
            {breakdown.chatQueries !== undefined && breakdown.chatQueries > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t("cost.chatQueries")}</span>
                <span className="font-medium">{formatCost(breakdown.chatQueries)}</span>
              </div>
            )}
            {breakdown.rerunCaptions !== undefined && breakdown.rerunCaptions > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t("cost.rerunCaptions")}</span>
                <span className="font-medium">{formatCost(breakdown.rerunCaptions)}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
          <TrendingUp className="w-3 h-3" />
          <span>{t("cost.updatesRealtime")}</span>
        </div>
      </CardContent>
    </Card>
  );
}
